import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { v4 as uuidv4 } from "uuid";
import './App.css';

function LinkSparkAssistant() {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [input, setInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [currentUtter, setCurrentUtter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  // Load chats from localStorage on mount
  useEffect(() => {
    const savedChats = JSON.parse(localStorage.getItem("linkSparkChats")) || [];
    setChats(savedChats);
    if (savedChats.length > 0) {
      setActiveChatId(savedChats[0].id);
    }
  }, []);

  // Save chats to localStorage when updated
  useEffect(() => {
    if (chats.length > 0) {
      localStorage.setItem("linkSparkChats", JSON.stringify(chats));
    }
  }, [chats]);

  const activeChat = chats.find(c => c.id === activeChatId);

  // Create new chat
  const newChat = () => {
    const newId = uuidv4();
    const newChatObj = {
      id: newId,
      title: "New Conversation",
      messages: [],
      createdAt: Date.now(),
      starred: false
    };
    setChats(prev => [newChatObj, ...prev]);
    setActiveChatId(newId);
    setInput('');
    setImageFile(null);
  };

  // Delete chat
  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    setChats(prev => prev.filter(c => c.id !== chatId));
    if (activeChatId === chatId) {
      const remainingChats = chats.filter(c => c.id !== chatId);
      setActiveChatId(remainingChats.length > 0 ? remainingChats[0].id : null);
    }
  };

  // Update messages inside active chat
  const updateChatMessages = (chatId, newMessages) => {
    setChats(prev =>
      prev.map(c =>
        c.id === chatId
          ? {
              ...c,
              messages: newMessages,
              title:
                c.title === "New Conversation" && newMessages[0]?.text
                  ? newMessages[0].text.slice(0, 40) + (newMessages[0].text.length > 40 ? "..." : "")
                  : c.title
            }
          : c
      )
    );
  };

  // Toggle star
  const toggleStar = (chatId, e) => {
    e.stopPropagation();
    setChats(prev =>
      prev.map(c =>
        c.id === chatId ? { ...c, starred: !c.starred } : c
      )
    );
  };

  // Voice Input
  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert("Voice input not supported in this browser.");
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };
    recognition.start();
  };

  // Text to Speech
  const speakText = (text) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-US";
    utter.rate = 0.9;
    utter.pitch = 1;
    setCurrentUtter(utter);
    synth.speak(utter);
  };

  const stopSpeaking = () => window.speechSynthesis.cancel();

  // Handle file upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  // Send Message
  const handleSendMessage = async (customText) => {
    if (!activeChatId && chats.length === 0) {
      newChat();
      return;
    }

    const currentChatId = activeChatId || chats[0]?.id;
    const textToSend = customText || input;
    
    if (!textToSend.trim() && !imageFile) return;

    setIsLoading(true);
    
    const userMsg = {
      id: uuidv4(),
      text: textToSend || "[Image Uploaded]",
      sender: 'user',
      timestamp: Date.now(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    const currentChat = chats.find(c => c.id === currentChatId);
    const newMessages = [...(currentChat?.messages || []), userMsg];
    updateChatMessages(currentChatId, newMessages);
    setInput('');

    try {
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
      
      const response = await model.generateContent([
        {
          text: `You are LinkSpark, a LinkedIn growth assistant. Help with LinkedIn content creation, networking strategies, profile optimization, and professional growth. Provide actionable advice in a friendly, professional tone. User's message: ${textToSend}`
        }
      ]);
      
      const botReply = response.response.text();

      const botMsg = {
        id: uuidv4(),
        text: botReply,
        sender: 'bot',
        timestamp: Date.now(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      updateChatMessages(currentChatId, [...newMessages, botMsg]);
      speakText(botReply);
      
    } catch (err) {
      console.error("Gemini error", err);
      const errorMsg = {
        id: uuidv4(),
        text: "Sorry, I encountered an error. Please try again.",
        sender: 'bot',
        timestamp: Date.now(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      updateChatMessages(currentChatId, [...newMessages, errorMsg]);
    } finally {
      setIsLoading(false);
      setImageFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatMessage = (text) => {
    // Simple markdown-like formatting
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>');
  };

  return (
    <div className="linkspark-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">⚡</div>
            {!sidebarCollapsed && <span className="logo-text">LinkSpark</span>}
          </div>
          <button 
            className="collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {!sidebarCollapsed && (
          <>
            <button className="new-chat-btn" onClick={newChat}>
              <span className="btn-icon">+</span>
              New Chat
            </button>

            <div className="chat-history">
              <div className="history-header">Recent Chats</div>
              {chats.map(chat => (
                <div
                  key={chat.id}
                  className={`chat-item ${activeChatId === chat.id ? 'active' : ''}`}
                  onClick={() => setActiveChatId(chat.id)}
                >
                  <div className="chat-content">
                    <div className="chat-title">{chat.title}</div>
                    <div className="chat-preview">
                      {chat.messages.length > 0 
                        ? chat.messages[chat.messages.length - 1].text.slice(0, 30) + '...'
                        : 'No messages yet'
                      }
                    </div>
                  </div>
                  <div className="chat-actions">
                    <button
                      className={`star-btn ${chat.starred ? 'starred' : ''}`}
                      onClick={(e) => toggleStar(chat.id, e)}
                    >
                      ★
                    </button>
                    <button
                      className="delete-btn"
                      onClick={(e) => deleteChat(chat.id, e)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="main-content">
        <div className="chat-header">
          <h1 className="app-title">
            <span className="title-icon">⚡</span>
            LinkSpark Assistant
          </h1>
          <p className="app-subtitle">Your LinkedIn Growth & Networking Companion</p>
        </div>

        <div className="messages-container">
          {!activeChat || activeChat.messages.length === 0 ? (
            <div className="welcome-screen">
              <div className="welcome-icon">🚀</div>
              <h2>Welcome to LinkSpark!</h2>
              <p>I'm here to help you grow your LinkedIn presence and professional network.</p>
              <div className="example-prompts">
                <div className="prompt-category">
                  <h4>💡 Content Ideas</h4>
                  <button onClick={() => setInput("Help me write a LinkedIn post about career growth")}>
                    Write a career growth post
                  </button>
                  <button onClick={() => setInput("Generate ideas for industry insights posts")}>
                    Industry insights ideas
                  </button>
                </div>
                <div className="prompt-category">
                  <h4>🤝 Networking</h4>
                  <button onClick={() => setInput("How to write effective connection requests")}>
                    Connection request tips
                  </button>
                  <button onClick={() => setInput("Best practices for LinkedIn messaging")}>
                    LinkedIn messaging guide
                  </button>
                </div>
                <div className="prompt-category">
                  <h4>📈 Profile Optimization</h4>
                  <button onClick={() => setInput("Optimize my LinkedIn headline")}>
                    Optimize headline
                  </button>
                  <button onClick={() => setInput("LinkedIn summary best practices")}>
                    Summary best practices
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="messages-list">
              {activeChat.messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.sender}`}>
                  <div className="message-avatar">
                    {msg.sender === 'user' ? '👤' : '⚡'}
                  </div>
                  <div className="message-content">
                    <div 
                      className="message-text"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                    />
                    <div className="message-time">{msg.time}</div>
                    {msg.sender === 'bot' && (
                      <button 
                        className="speak-btn"
                        onClick={() => speakText(msg.text)}
                        title="Read aloud"
                      >
                        🔊
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message bot">
                  <div className="message-avatar">⚡</div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="input-container">
          <div className="input-wrapper">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              placeholder="Ask LinkSpark anything about LinkedIn growth..."
              className="message-input"
              disabled={isLoading}
            />
            <div className="input-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="action-btn file-btn" title="Upload image">
                📎
              </label>
              <button
                onClick={startVoiceInput}
                className="action-btn voice-btn"
                title="Voice input"
                disabled={isLoading}
              >
                🎤
              </button>
              <button
                onClick={() => handleSendMessage()}
                className="action-btn send-btn"
                disabled={isLoading || (!input.trim() && !imageFile)}
              >
                {isLoading ? '⏳' : '→'}
              </button>
            </div>
          </div>
          {imageFile && (
            <div className="file-preview">
              <span>📷 {imageFile.name}</span>
              <button onClick={() => setImageFile(null)}>×</button>
            </div>
          )}
          <div className="audio-controls">
            <button onClick={stopSpeaking} className="audio-btn">⏹ Stop</button>
            <button onClick={() => currentUtter && speakText(currentUtter.text)} className="audio-btn">🔄 Replay</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LinkSparkAssistant;